<template>
  <section class="tavalod" :class="{ 'party-started': partyStarted }">
    <!-- ================= TOP BAR ================= -->
    <header class="topbar">
      <div class="logo-mark">
        بندیتو
        <span class="dot"></span>
      </div>

      <a href="#gifts" class="cta-mini">
        <span>هدیه‌ها رو ببین</span>
        <span class="mini-arrow">←</span>
      </a>
    </header>

    <!-- ================= HERO ================= -->
    <section class="hero">
      <div class="hero-bg-orb orb-1"></div>
      <div class="hero-bg-orb orb-2"></div>
      <div class="hero-bg-orb orb-3"></div>

      <div class="sparkles">
        <span v-for="n in 14" :key="n" :class="`spark spark-${n}`"> ✦ </span>
      </div>

      <div class="hero-inner">
        <span class="eyebrow">
          <span class="eyebrow-icon d-flex">🎂</span>
          جشن ۱۱ سالگی بندیتو
          <span class="eyebrow-pulse"></span>
        </span>

        <!-- ================= CAKE ================= -->
        <div class="cake-stage">
          <div class="cake-shadow"></div>

          <div class="cake">
            <!-- candles -->
            <div class="candles">
              <div
                v-for="(candle, index) in candles"
                :key="index"
                class="candle-wrap"
                :class="{ extinguished: !candle.lit }"
              >
                <div class="flame">
                  <span class="flame-inner"></span>
                </div>

                <div class="smoke">
                  <i></i>
                  <i></i>
                  <i></i>
                </div>

                <div class="candle">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>

            <!-- cake -->
            <div class="cake-top">
              <div class="cream cream-1"></div>
              <div class="cream cream-2"></div>
              <div class="cream cream-3"></div>

              <span class="berry b1"></span>
              <span class="berry b2"></span>
              <span class="berry b3"></span>
            </div>

            <div class="cake-layer layer-1">
              <div class="drip d1"></div>
              <div class="drip d2"></div>
              <div class="drip d3"></div>
              <div class="drip d4"></div>
            </div>

            <div class="cake-layer layer-2">
              <div class="cream-line"></div>
              <span class="cake-dot cd1"></span>
              <span class="cake-dot cd2"></span>
              <span class="cake-dot cd3"></span>
              <span class="cake-dot cd4"></span>
              <span class="cake-dot cd5"></span>
            </div>

            <div class="cake-layer layer-3">
              <div class="cake-ribbon"></div>
            </div>

            <div class="cake-plate"></div>
          </div>

          <!-- blow button -->
          <button
            v-if="!partyStarted"
            class="blow-button"
            type="button"
            @click="blowCandles"
          >
            <span class="blow-icon">💨</span>

            <span class="blow-text">
              <strong>فوت کن!</strong>
              <small>شمع‌ها رو خاموش کن</small>
            </span>

            <span class="blow-ring"></span>
          </button>

          <!-- after party -->
          <div v-else class="party-message">
            <span class="party-message-icon">🎉</span>
            <div class="d-flex flex-column">
              <strong>تولدت مبارک بندیتو!</strong>
              <span>جشن شروع شد ✨</span>
            </div>
          </div>
        </div>

        <h1>
          ۱۱ ساله که
          <em>کنار همیم</em>؛
          <br />
          امسال تولدمون رو با هدیه‌دادن به تو جشن می‌گیریم
          <span class="heart">♥</span>
        </h1>

        <p class="lead">
          به‌جای این‌که ما جشن بگیریم، هدیه‌ش رسیده به تو. محصولات محبوب بندیتو
          با تخفیف‌های ویژه منتظر تو هستن.
        </p>

        <a href="#gifts" class="cta-primary">
          <span class="cta-gift">🎁</span>
          <span>وارد جشن شو</span>
          <span class="arrow">←</span>
        </a>

        <div class="scroll-hint">
          <span>ماجرا رو ببین</span>
          <span class="chev"></span>
        </div>
      </div>

      <!-- balloons -->
      <div class="balloons" aria-hidden="true">
        <div class="balloon balloon-1"><span></span></div>
        <div class="balloon balloon-2"><span></span></div>
        <div class="balloon balloon-3"><span></span></div>
        <div class="balloon balloon-4"><span></span></div>
        <div class="balloon balloon-5"><span></span></div>

        <div class="balloon balloon-6"><span></span></div>
        <div class="balloon balloon-7"><span></span></div>
        <div class="balloon balloon-8"><span></span></div>
        <div class="balloon balloon-9"><span></span></div>
        <div class="balloon balloon-10"><span></span></div>
        <div class="balloon balloon-11"><span></span></div>
        <div class="balloon balloon-12"><span></span></div>
      </div>

      <!-- confetti -->
      <div class="confetti-container" aria-hidden="true">
        <span
          v-for="n in 110"
          :key="n"
          class="confetti"
          :style="getConfettiStyle(n)"
        ></span>
      </div>

      <!-- fireworks -->
      <div class="fireworks" aria-hidden="true">
        <div class="firework firework-1">
          <i v-for="n in 16" :key="n"></i>
        </div>

        <div class="firework firework-2">
          <i v-for="n in 16" :key="n"></i>
        </div>

        <div class="firework firework-3">
          <i v-for="n in 16" :key="n"></i>
        </div>

        <div class="firework firework-4">
          <i v-for="n in 16" :key="n"></i>
        </div>

        <div class="firework firework-5">
          <i v-for="n in 16" :key="n"></i>
        </div>

        <div class="firework firework-6">
          <i v-for="n in 16" :key="n"></i>
        </div>
      </div>
    </section>

    <!-- ================= STEPS ================= -->
    <section class="steps-section">
      <div class="wrap">
        <div class="steps-head">
          <span class="section-kicker">🎁 هدیه مخصوص تو</span>

          <h2>هدیه تولد بندیتو برای تو</h2>

          <p>سه قدم ساده، بین تو و هدیه‌هایی که امسال برات آماده کردیم.</p>
        </div>

        <div class="steps">
          <div class="step">
            <div class="step-number">۰۱</div>

            <div class="step-icon-wrap">
              <span class="step-icon">🛒</span>
            </div>

            <div class="step-content">
              <span class="step-label">قدم اول</span>

              <h3>سبدت رو پر کن</h3>

              <p>
                با خرید بالای ۲٫۵۰۰٫۰۰۰ تومان، جشن تولد بندیتو برای تو فعال
                می‌شه.
              </p>
            </div>

            <span class="step-arrow">←</span>
          </div>

          <div class="step highlight">
            <div class="step-number">۰۲</div>

            <div class="step-icon-wrap">
              <span class="step-icon">✨</span>
            </div>

            <div class="step-content">
              <span class="step-label">قدم دوم</span>

              <h3>هدیه‌ها فعال می‌شن</h3>

              <p>
                محصولات منتخب بندیتو با تخفیف‌های ویژه تا ۹۹٪ در سبد خریدت روشن
                می‌شن.
              </p>
            </div>

            <span class="step-arrow">←</span>
          </div>

          <div class="step">
            <div class="step-number">۰۳</div>

            <div class="step-icon-wrap">
              <span class="step-icon">🎁</span>
            </div>

            <div class="step-content">
              <span class="step-label">قدم سوم</span>

              <h3>با ۱٬۰۰۰ تومان ببرشون</h3>

              <p>
                بعضی از محصولات محبوبت رو فقط با ۱٬۰۰۰ تومان به سبد خریدت اضافه
                کن.
              </p>
            </div>

            <span class="step-arrow">←</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ================= GIFTS ================= -->
    <section class="gifts-section" id="gifts">
      <div class="gift-background"></div>

      <div class="wrap">
        <div class="gifts-head">
          <span class="section-kicker">🎀 Mystery Gifts</span>

          <h2>
            هدیه‌ها رو
            <em>تو سایت</em>
            پیدا کن
          </h2>

          <p>
            نمی‌گیم کدوماَن؛ توی سایت منتظرتن. هر بسته یه غافلگیریه؛ بعضیاشون تا
            ۹۹٪ تخفیف دارن.
          </p>
        </div>

        <div class="gift-grid">
          <div
            v-for="(gift, index) in gifts"
            :key="index"
            class="gift"
            :class="`gift-${index + 1}`"
          >
            <div class="gift-shadow"></div>

            <div class="box">
              <div class="box-top"></div>

              <div class="box-side side-left"></div>
              <div class="box-side side-right"></div>

              <div class="ribbon-v"></div>
              <div class="ribbon-h"></div>

              <div class="bow">
                <span class="bow-left"></span>
                <span class="bow-right"></span>
                <span class="bow-center"></span>
              </div>

              <div class="box-shine"></div>
            </div>

            <span class="tag">{{ gift.tag }}</span>

            <div class="peek">
              <span class="peek-icon">{{ gift.icon }}</span>
              <strong>{{ gift.title }}</strong>
              <small>{{ gift.text }}</small>
            </div>
          </div>
        </div>

        <p class="gifts-note">
          <span>✦</span>
          هدیه‌ها فقط با خرید بالای ۲٫۵۰۰٫۰۰۰ تومان فعال می‌شن
          <span>✦</span>
        </p>
      </div>
    </section>

    <!-- ================= FINAL CTA ================= -->
    <section class="final-banner">
      <div class="final-glow final-glow-1"></div>
      <div class="final-glow final-glow-2"></div>

      <div class="final-particles">
        <span v-for="n in 20" :key="n">✦</span>
      </div>

      <div class="inner">
        <span class="eyebrow final-eyebrow">
          <span>🎈</span>
          محدود به مدت جشن تولد
          <span>🎈</span>
        </span>

        <div class="final-cake-icon">🎂</div>

        <h2>
          تولد ماست،
          <strong>هدیه‌ش برای توئه!</strong>
        </h2>

        <p>
          وارد سایت بندیتو شو، سبدت رو پر کن و ببین امسال چه هدیه‌هایی منتظرتن.
        </p>

        <a href="#" class="final-button">
          <span class="final-button-glow"></span>
          <span class="final-button-icon">🎁</span>
          <span>وارد جشن شو</span>
          <span class="final-arrow">←</span>
        </a>
      </div>
    </section>

    <!-- ================= FOOTER ================= -->
    <footer>
      <div class="logo-mark">
        بندیتو
        <span class="dot"></span>
      </div>

      <span> ۱۱ سال کنار تو، با محصولاتی که دوستشون داری. </span>
    </footer>

    <!-- mobile CTA -->
    <div class="sticky-mobile">
      <a href="#gifts">
        <span>🎁</span>
        وارد جشن شو
        <span>←</span>
      </a>
    </div>
  </section>
</template>

<script setup>
import { ref } from "vue";

const partyStarted = ref(false);

const candles = ref([
  { lit: true },
  { lit: true },
  { lit: true },
  { lit: true },
  { lit: true },
]);

const gifts = [
  {
    tag: "؟",
    icon: "🎁",
    title: "شاید همینه!",
    text: "محصول محبوبت",
  },
  {
    tag: "٪",
    icon: "✨",
    title: "یکی از هدیه‌ها",
    text: "همینجاست",
  },
  {
    tag: "۹۹٪",
    icon: "🔥",
    title: "تا ۹۹٪ تخفیف",
    text: "روی بعضی بسته‌ها",
  },
  {
    tag: "؟",
    icon: "🔎",
    title: "بگرد، پیداش کن",
    text: "یه سورپرایز منتظرته",
  },
  {
    tag: "۱٬۰۰۰ت",
    icon: "💎",
    title: "فقط ۱٬۰۰۰ تومان",
    text: "بعضی هدیه‌ها",
  },
  {
    tag: "؟",
    icon: "🎀",
    title: "هر بسته یه",
    text: "غافلگیریه",
  },
  {
    tag: "٪",
    icon: "🎉",
    title: "شانستو امتحان کن",
    text: "شاید برنده باشی",
  },
  {
    tag: "؟",
    icon: "💝",
    title: "تو سایت منتظرته",
    text: "هدیه مخصوص تو",
  },
];

const blowCandles = () => {
  if (partyStarted.value) return;

  // خاموش شدن شمع‌ها
  candles.value.forEach((candle, index) => {
    setTimeout(() => {
      candle.lit = false;
    }, index * 100);
  });

  // شروع جشن
  setTimeout(() => {
    partyStarted.value = true;
  }, 650);
};
const getConfettiStyle = (index) => {
  const left = Math.random() * 100;
  const delay = Math.random() * 1.4;
  const duration = 2.8 + Math.random() * 2.8;
  const rotate = Math.random() * 360;

  return {
    "--left": `${left}%`,
    "--delay": `${delay}s`,
    "--duration": `${duration}s`,
    "--rotate": `${rotate}deg`,
  };
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Lalezar&family=Vazirmatn:wght@400;500;600;700;800;900&display=swap");

/* =========================================================
   ROOT
========================================================= */

.tavalod {
  --cream: #fff7f4;
  --cream-2: #f9e8e5;

  --pink: #e8577c;
  --pink-2: #f58da6;
  --pink-light: #ffc3d1;

  --pink-deep: #7a1440;
  --pink-dark: #4c0927;

  --gold: #d1a14a;
  --gold-light: #f6df9e;

  --purple: #8b4f88;

  --ink: #2b1220;
  --ink-soft: #866a76;

  --white: #fff;

  width: 100%;
  min-height: 100vh;
  overflow: hidden;

  background: radial-gradient(
      circle at 10% 5%,
      rgba(232, 87, 124, 0.08),
      transparent 30%
    ),
    linear-gradient(180deg, var(--cream), #fff);
  color: var(--ink);

  font-family: "Vazirmatn", sans-serif;
  direction: rtl;
}

.tavalod *,
.tavalod *::before,
.tavalod *::after {
  box-sizing: border-box;
}

.tavalod a {
  text-decoration: none;
  color: inherit;
}

.tavalod button {
  border: 0;
  font-family: inherit;
  cursor: pointer;
}

.wrap {
  width: 100%;
  max-width: 1120px;
  margin-inline: auto;
  padding-inline: 24px;
}

/* =========================================================
   TOPBAR
========================================================= */

.topbar {
  position: sticky;
  top: 0;
  z-index: 100;

  height: 74px;
  padding-inline: 28px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  background: rgba(255, 247, 244, 0.78);
  backdrop-filter: blur(18px);

  border-bottom: 1px solid rgba(122, 20, 64, 0.08);
}

.logo-mark {
  display: flex;
  align-items: center;
  gap: 9px;

  font-family: "Lalezar", sans-serif !important;
  font-size: 25px;
  color: var(--pink-deep);
}

.logo-mark .dot {
  width: 9px;
  height: 9px;

  border-radius: 50%;
  background: var(--pink);

  box-shadow: 0 0 0 5px rgba(232, 87, 124, 0.1);
}

.cta-mini {
  position: relative;

  display: flex;
  align-items: center;
  gap: 9px;

  padding: 11px 20px;

  border-radius: 999px;

  color: #fff !important;

  background: linear-gradient(135deg, var(--pink), var(--pink-deep));

  font-size: 14px;
  font-weight: 700;

  box-shadow: 0 8px 20px rgba(122, 20, 64, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);

  overflow: hidden;

  transition: 0.3s ease;
}

.cta-mini::before {
  content: "";

  position: absolute;
  inset: 0;

  background: linear-gradient(
    110deg,
    transparent 25%,
    rgba(255, 255, 255, 0.4) 50%,
    transparent 75%
  );

  transform: translateX(-120%);
  transition: transform 0.7s ease;
}

.cta-mini:hover::before {
  transform: translateX(120%);
}

.cta-mini:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 28px rgba(122, 20, 64, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
}

.mini-arrow {
  font-size: 17px;
}

/* =========================================================
   HERO
========================================================= */

.hero {
  position: relative;

  min-height: 920px;
  padding: 80px 24px 120px;

  text-align: center;

  overflow: hidden;

  isolation: isolate;
}

.hero::after {
  content: "";

  position: absolute;
  inset: auto 0 0;

  height: 180px;

  background: linear-gradient(to bottom, transparent, var(--cream));

  z-index: -1;
}

.hero-bg-orb {
  position: absolute;

  width: 400px;
  height: 400px;

  border-radius: 50%;

  filter: blur(70px);

  opacity: 0.5;

  pointer-events: none;

  z-index: -3;
}

.orb-1 {
  top: 80px;
  right: -180px;
  background: rgba(232, 87, 124, 0.25);
}

.orb-2 {
  top: 360px;
  left: -220px;
  background: rgba(199, 154, 70, 0.2);
}

.orb-3 {
  bottom: -180px;
  right: 30%;
  background: rgba(139, 79, 136, 0.15);
}

.hero-inner {
  position: relative;
  z-index: 20;

  width: 100%;
  max-width: 820px;

  margin-inline: auto;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;

  padding: 9px 20px;

  border: 1px solid rgba(232, 87, 124, 0.2);
  border-radius: 999px;

  color: var(--pink-deep);

  background: rgba(232, 87, 124, 0.08);

  font-size: 20px;
  font-weight: 700;

  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7),
    0 8px 24px rgba(122, 20, 64, 0.06);
}

.eyebrow-icon {
  font-size: 24px;
}

.eyebrow-pulse {
  width: 7px;
  height: 7px;

  border-radius: 50%;

  background: #53bd71;

  box-shadow: 0 0 0 5px rgba(83, 189, 113, 0.12);

  animation: pulse-dot 1.5s infinite;
}

@keyframes pulse-dot {
  50% {
    transform: scale(0.75);
    opacity: 0.55;
  }
}

/* =========================================================
   SPARKLES
========================================================= */

.sparkles {
  position: absolute;
  inset: 0;

  pointer-events: none;
  z-index: 1;
}

.spark {
  position: absolute;

  color: var(--gold);

  font-size: 20px;

  opacity: 0.55;

  animation: sparkle-float 3s ease-in-out infinite;
}

.spark-1 {
  top: 14%;
  right: 18%;
}

.spark-2 {
  top: 20%;
  left: 14%;
  animation-delay: 0.4s;
}

.spark-3 {
  top: 38%;
  right: 9%;
  font-size: 13px;
  animation-delay: 1s;
}

.spark-4 {
  top: 48%;
  left: 10%;
  animation-delay: 1.4s;
}

.spark-5 {
  top: 62%;
  right: 20%;
  font-size: 12px;
}

.spark-6 {
  top: 70%;
  left: 22%;
  animation-delay: 0.8s;
}

.spark-7 {
  top: 28%;
  right: 31%;
  font-size: 11px;
}

.spark-8 {
  top: 30%;
  left: 30%;
  animation-delay: 0.7s;
}

.spark-9 {
  top: 76%;
  right: 10%;
  animation-delay: 1.2s;
}

.spark-10 {
  top: 83%;
  left: 13%;
  font-size: 14px;
}

.spark-11 {
  top: 12%;
  left: 38%;
}

.spark-12 {
  top: 45%;
  right: 25%;
  font-size: 10px;
}

.spark-13 {
  top: 57%;
  left: 28%;
}

.spark-14 {
  top: 22%;
  right: 7%;
  font-size: 13px;
}

@keyframes sparkle-float {
  0%,
  100% {
    opacity: 0.25;
    transform: translateY(0) rotate(0) scale(0.8);
  }

  50% {
    opacity: 1;
    transform: translateY(-12px) rotate(90deg) scale(1.2);
  }
}

/* =========================================================
   LUXURY BIRTHDAY CAKE
========================================================= */

.cake-stage {
  position: relative;
  width: min(500px, 92vw);
  margin: 48px auto 0;
  min-height: 430px;

  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
}

.cake-shadow {
  position: absolute;
  bottom: 48px;
  left: 50%;

  width: 330px;
  height: 42px;

  transform: translateX(-50%);

  border-radius: 50%;

  background: rgba(91, 12, 45, 0.2);
  filter: blur(17px);

  opacity: 0.8;
}

/* cake */

.cake {
  position: relative;

  width: min(390px, 82vw);

  margin-top: 82px;

  filter: drop-shadow(0 25px 25px rgba(91, 12, 45, 0.16))
    drop-shadow(0 4px 7px rgba(232, 87, 124, 0.08));

  animation: cake-float 4s ease-in-out infinite;

  z-index: 10;
}

@keyframes cake-float {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-8px);
  }
}

/* =========================
   CANDLES
========================= */

.candles {
  position: absolute;

  top: -76px;
  left: 50%;

  width: 225px;

  transform: translateX(-50%);

  display: flex;
  justify-content: space-between;

  z-index: 30;
}

.candle-wrap {
  position: relative;

  width: 26px;
  height: 92px;
}

.candle {
  position: absolute;

  bottom: 0;
  left: 50%;

  width: 25px;
  height: 72px;

  transform: translateX(-50%);

  border-radius: 8px 8px 5px 5px;

  background: repeating-linear-gradient(-55deg, #fff 0 8px, #f4a0b4 8px 15px);

  box-shadow: inset -4px 0 8px rgba(120, 20, 65, 0.15),
    inset 3px 0 5px rgba(255, 255, 255, 0.7), 0 6px 12px rgba(91, 12, 45, 0.16);
}

.candle::before {
  content: "";

  position: absolute;

  top: -3px;
  left: 50%;

  width: 6px;
  height: 7px;

  transform: translateX(-50%);

  border-radius: 50%;

  background: #5c2637;
}

.candle span {
  position: absolute;

  left: 3px;
  right: 3px;

  height: 2px;

  border-radius: 999px;

  background: rgba(255, 255, 255, 0.55);
}

.candle span:nth-child(1) {
  top: 18px;
}

.candle span:nth-child(2) {
  top: 40px;
}

.candle span:nth-child(3) {
  top: 60px;
}

/* flame */

.flame {
  position: absolute;

  top: -22px;
  left: 50%;

  width: 21px;
  height: 30px;

  transform: translateX(-50%);

  border-radius: 60% 40% 60% 40%;

  background: radial-gradient(
    circle at 50% 72%,
    #fff 0 14%,
    #ffe986 25%,
    #ffac3d 48%,
    #f05a24 70%,
    transparent 72%
  );

  filter: drop-shadow(0 0 7px #ffcc58)
    drop-shadow(0 0 18px rgba(255, 135, 35, 0.65));

  animation: flame 0.65s ease-in-out infinite alternate;
}

.flame-inner {
  position: absolute;

  left: 50%;
  bottom: 4px;

  width: 7px;
  height: 12px;

  transform: translateX(-50%);

  border-radius: 50%;

  background: #fffde6;
}

@keyframes flame {
  from {
    transform: translateX(-50%) rotate(-5deg) scale(0.94);
  }

  to {
    transform: translateX(-50%) rotate(5deg) scale(1.08);
  }
}

/* smoke */

.smoke {
  position: absolute;

  top: -24px;
  left: 50%;

  transform: translateX(-50%);

  opacity: 0;

  pointer-events: none;
}

.extinguished .flame {
  animation: blow-out 0.45s ease forwards;
}

.extinguished .smoke {
  animation: smoke-rise 1.8s ease forwards;
}

@keyframes blow-out {
  0% {
    opacity: 1;
    transform: translateX(-50%) scale(1);
  }

  40% {
    opacity: 0.8;
    transform: translateX(-70%) rotate(-30deg) scale(0.7);
  }

  100% {
    opacity: 0;
    transform: translateX(-120%) rotate(-55deg) scale(0.1);
  }
}

.smoke i {
  position: absolute;

  width: 10px;
  height: 10px;

  border-radius: 50%;

  background: rgba(110, 100, 105, 0.28);

  filter: blur(2px);
}

.smoke i:nth-child(1) {
  transform: translate(-5px, 0);
}

.smoke i:nth-child(2) {
  transform: translate(5px, -8px);
}

.smoke i:nth-child(3) {
  transform: translate(0, -17px);
}

@keyframes smoke-rise {
  0% {
    opacity: 0;
    transform: translate(-50%, 0) scale(0.5);
  }

  20% {
    opacity: 0.65;
  }

  100% {
    opacity: 0;
    transform: translate(-50%, -60px) scale(1.7);
  }
}

/* =========================
   CAKE TOP
========================= */

.cake-top {
  position: relative;

  width: 78%;
  height: 48px;

  margin-inline: auto;

  border-radius: 50%;

  background: radial-gradient(
    ellipse at 50% 25%,
    #ffffff 0 28%,
    #ffe8ed 48%,
    #efa0b4 100%
  );

  z-index: 8;

  box-shadow: inset 0 -8px 10px rgba(122, 20, 64, 0.12),
    0 -5px 10px rgba(255, 255, 255, 0.8);
}

/* cream decorations */

.cream {
  position: absolute;

  width: 48px;
  height: 22px;

  border-radius: 50%;

  background: radial-gradient(ellipse at 40% 30%, #fff, #fff7f5 70%, #f5dfe3);

  box-shadow: 0 4px 7px rgba(122, 20, 64, 0.1);
}

.cream-1 {
  left: 17%;
  top: 6px;
}

.cream-2 {
  left: 43%;
  top: 13px;
}

.cream-3 {
  right: 15%;
  top: 5px;
}

/* strawberries */

.berry {
  position: absolute;

  width: 18px;
  height: 20px;

  border-radius: 50% 50% 45% 45%;

  background: radial-gradient(circle at 30% 25%, #fff, transparent 13%),
    linear-gradient(145deg, #ff829e, #bd2457);

  box-shadow: 0 5px 7px rgba(122, 20, 64, 0.2);
}

.berry::after {
  content: "";

  position: absolute;

  top: -5px;
  left: 50%;

  width: 14px;
  height: 8px;

  transform: translateX(-50%);

  border-radius: 50%;

  background: #67a85b;
}

.b1 {
  left: 29%;
  top: 12px;
}

.b2 {
  right: 29%;
  top: 9px;
}

.b3 {
  left: 51%;
  top: 19px;
}

/* =========================
   CAKE LAYERS
========================= */

.cake-layer {
  position: relative;

  margin-inline: auto;

  overflow: visible;
}

.layer-1 {
  width: 75%;
  height: 82px;

  margin-top: -5px;

  border-radius: 18px 18px 14px 14px;

  background: linear-gradient(180deg, #ffb8c8 0%, #ee6f91 48%, #c83261 100%);

  box-shadow: inset 0 7px 10px rgba(255, 255, 255, 0.28),
    inset 0 -10px 0 rgba(110, 13, 55, 0.14);
}

.layer-2 {
  width: 90%;
  height: 83px;

  margin-top: -2px;

  border-radius: 14px;

  background: linear-gradient(180deg, #ffd2dc, #ef86a3 60%, #db5b80);

  box-shadow: inset 0 6px 9px rgba(255, 255, 255, 0.28),
    inset 0 -8px 0 rgba(122, 20, 64, 0.12);
}

.layer-3 {
  width: 100%;
  height: 84px;

  margin-top: -2px;

  border-radius: 14px 14px 20px 20px;

  background: linear-gradient(180deg, #f8e1a1, #d6a64e 58%, #a97624);

  box-shadow: inset 0 6px 9px rgba(255, 255, 255, 0.3),
    inset 0 -9px 0 rgba(103, 67, 17, 0.14);
}

/* white cream dripping */

.drip {
  position: absolute;

  top: -1px;

  width: 15px;
  height: 38px;

  border-radius: 0 0 18px 18px;

  background: linear-gradient(180deg, #fff, #fff4f5);

  box-shadow: 0 4px 6px rgba(122, 20, 64, 0.1);
}

.d1 {
  left: 12%;
  height: 34px;
}

.d2 {
  left: 36%;
  height: 49px;
}

.d3 {
  right: 31%;
  height: 35px;
}

.d4 {
  right: 11%;
  height: 45px;
}

.cream-line {
  position: absolute;

  left: 8%;
  right: 8%;
  top: 12px;

  height: 8px;

  border-radius: 999px;

  background: rgba(255, 255, 255, 0.72);

  box-shadow: 0 2px 5px rgba(122, 20, 64, 0.08);
}

.cake-dot {
  position: absolute;

  width: 10px;
  height: 10px;

  border-radius: 50%;

  background: radial-gradient(circle at 30% 25%, #fff, #f8df9c);

  box-shadow: 0 2px 5px rgba(122, 20, 64, 0.15);
}

.cd1 {
  left: 17%;
  top: 43px;
}

.cd2 {
  left: 34%;
  top: 50px;
}

.cd3 {
  left: 51%;
  top: 41px;
}

.cd4 {
  right: 28%;
  top: 48px;
}

.cd5 {
  right: 14%;
  top: 39px;
}

/* gold ribbon */

.cake-ribbon {
  position: absolute;

  top: 18px;
  left: 0;
  right: 0;

  height: 13px;

  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.5),
    rgba(255, 255, 255, 0.18)
  );

  box-shadow: 0 3px 5px rgba(103, 67, 17, 0.15);
}

/* plate */

.cake-plate {
  position: relative;

  width: 116%;
  height: 24px;
  left: 8%;
  margin: 5px auto 0;

  border-radius: 50%;

  background: linear-gradient(180deg, #fff, #eee3e6);

  box-shadow: 0 9px 13px rgba(122, 20, 64, 0.13),
    inset 0 4px 5px rgba(255, 255, 255, 0.95);
}

/* party animation */

.party-started .cake {
  animation: cake-party 0.9s ease, cake-float 4s 1s ease-in-out infinite;
}

@keyframes cake-party {
  0% {
    transform: translateY(0) rotate(0);
  }

  25% {
    transform: translateY(-16px) rotate(-3deg);
  }

  50% {
    transform: translateY(-7px) rotate(3deg);
  }

  75% {
    transform: translateY(-12px) rotate(-2deg);
  }

  100% {
    transform: translateY(0) rotate(0);
  }
}
/* =========================================================
   BLOW BUTTON
========================================================= */

.blow-button {
  position: absolute;

  bottom: -30px;
  left: 50%;

  transform: translateX(-50%);

  min-width: 235px;
  min-height: 70px;

  padding: 10px 18px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  border-radius: 22px;

  color: #fff;

  background: linear-gradient(135deg, #8e204f, #e8577c);

  box-shadow: 0 15px 35px rgba(122, 20, 64, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);

  overflow: hidden;

  z-index: 40;

  animation: button-float 2.5s ease-in-out infinite,
    button-glow 2s ease-in-out infinite;

  transition: 0.25s ease;
}

.blow-button:hover {
  transform: translateX(-50%) translateY(-5px) scale(1.03);
}

.blow-button:active {
  transform: translateX(-50%) scale(0.97);
}

.blow-button::before {
  content: "";

  position: absolute;
  inset: 0;

  background: linear-gradient(
    120deg,
    transparent 20%,
    rgba(255, 255, 255, 0.4) 50%,
    transparent 80%
  );

  transform: translateX(-130%);

  animation: button-shine 2.8s ease-in-out infinite;
}

.blow-icon {
  position: relative;
  z-index: 2;

  width: 43px;
  height: 43px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 14px;

  background: rgba(255, 255, 255, 0.16);

  font-size: 23px;
}

.blow-text {
  position: relative;
  z-index: 2;

  display: flex;
  flex-direction: column;

  align-items: flex-start;
}

.blow-text strong {
  font-size: 17px;
}

.blow-text small {
  margin-top: 2px;

  font-size: 11px;

  opacity: 0.8;
}

.blow-ring {
  position: absolute;

  width: 130px;
  height: 130px;

  border: 1px solid rgba(255, 255, 255, 0.25);

  border-radius: 50%;

  animation: ring-pulse 2s infinite;
}

@keyframes ring-pulse {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }

  50% {
    opacity: 0.4;
  }

  100% {
    transform: scale(1.2);
    opacity: 0;
  }
}

@keyframes button-float {
  0%,
  100% {
    margin-bottom: 0;
  }

  50% {
    margin-bottom: 6px;
  }
}

@keyframes button-glow {
  0%,
  100% {
    box-shadow: 0 15px 35px rgba(122, 20, 64, 0.3), 0 0 0 rgba(232, 87, 124, 0);
  }

  50% {
    box-shadow: 0 18px 40px rgba(122, 20, 64, 0.35),
      0 0 30px rgba(232, 87, 124, 0.25);
  }
}

@keyframes button-shine {
  0% {
    transform: translateX(-130%);
  }

  35%,
  100% {
    transform: translateX(130%);
  }
}

/* =========================================================
   PARTY MESSAGE
========================================================= */

.party-message {
  position: absolute;

  bottom: -60px;
  left: 50%;

  transform: translateX(-50%);

  z-index: 45;

  min-width: 250px;

  padding: 13px 22px;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-radius: 20px;

  color: #fff;

  background: linear-gradient(135deg, var(--pink-deep), var(--pink));

  box-shadow: 0 15px 40px rgba(122, 20, 64, 0.3);

  animation: party-message-in 0.7s cubic-bezier(0.2, 1.3, 0.4, 1);
}

.party-message-icon {
  font-size: 28px;
}

.party-message strong {
  font-size: 16px;
}

.party-message span:last-child {
  font-size: 11px;
  opacity: 0.85;
}

@keyframes party-message-in {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px) scale(0.8);
  }

  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }
}

/* =========================================================
   PARTY BALLOONS
========================================================= */

.balloons {
  position: absolute;
  inset: 0;

  pointer-events: none;

  z-index: 10;
}

.balloon {
  position: absolute;

  width: 74px;
  height: 92px;

  border-radius: 50% 50% 45% 45%;

  opacity: 0;

  transform: translateY(130px) scale(0.35);

  transition: opacity 0.7s ease, transform 1.1s cubic-bezier(0.2, 1.5, 0.4, 1);

  box-shadow: inset -10px -12px 18px rgba(80, 10, 40, 0.16),
    inset 9px 8px 15px rgba(255, 255, 255, 0.2);
}

.party-started .balloon {
  opacity: 1;

  transform: translateY(0) scale(1);

  animation: balloon-float 4s ease-in-out infinite;
}

.balloon::before {
  content: "";

  position: absolute;

  top: 14px;
  left: 14px;

  width: 18px;
  height: 30px;

  border-radius: 50%;

  background: rgba(255, 255, 255, 0.32);

  transform: rotate(25deg);

  filter: blur(0.3px);
}

.balloon::after {
  content: "";

  position: absolute;

  bottom: -8px;
  left: 50%;

  transform: translateX(-50%);

  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 10px solid currentColor;
}

.balloon span {
  position: absolute;

  top: 100%;
  left: 50%;

  width: 1px;
  height: 100px;

  background: rgba(80, 20, 50, 0.25);

  transform: translateX(-50%);
}

/* colors */

.balloon-1 {
  top: 30%;
  left: 4%;

  color: #d83f70;

  background: linear-gradient(145deg, #ffb2c5, #c52d61);

  transition-delay: 0.05s;
}

.balloon-2 {
  top: 18%;
  right: 4%;

  color: #b07a20;

  background: linear-gradient(145deg, #ffe6a4, #bd8628);

  transition-delay: 0.12s;
}

.balloon-3 {
  top: 55%;
  right: 7%;

  color: #704069;

  background: linear-gradient(145deg, #c992c2, #713968);

  transition-delay: 0.18s;
}

.balloon-4 {
  top: 53%;
  left: 8%;

  color: #d94772;

  background: linear-gradient(145deg, #ffc1d0, #d54473);

  transition-delay: 0.24s;
}

.balloon-5 {
  top: 14%;
  left: 25%;

  width: 52px;
  height: 66px;

  color: #b98a30;

  background: linear-gradient(145deg, #fff0b7, #bd8b2f);

  transition-delay: 0.3s;
}

.balloon-6 {
  top: 38%;
  right: 20%;

  width: 57px;
  height: 72px;

  color: #b72f60;

  background: linear-gradient(145deg, #ffb1c4, #b92357);

  transition-delay: 0.36s;
}

.balloon-7 {
  top: 67%;
  left: 21%;

  width: 62px;
  height: 78px;

  color: #785071;

  background: linear-gradient(145deg, #d09ac7, #783c70);

  transition-delay: 0.42s;
}

.balloon-8 {
  top: 11%;
  right: 29%;

  width: 44px;
  height: 57px;

  color: #bc8528;

  background: linear-gradient(145deg, #ffe8a7, #bc8223);

  transition-delay: 0.48s;
}

.balloon-9 {
  top: 70%;
  right: 28%;

  width: 49px;
  height: 63px;

  color: #d64a76;

  background: linear-gradient(145deg, #ffb9ca, #c42d61);

  transition-delay: 0.54s;
}

.balloon-10 {
  top: 43%;
  left: 22%;

  width: 46px;
  height: 59px;

  color: #ae7d25;

  background: linear-gradient(145deg, #ffedac, #b77e20);

  transition-delay: 0.6s;
}

.balloon-11 {
  top: 25%;
  left: 36%;

  width: 38px;
  height: 49px;

  color: #793d70;

  background: linear-gradient(145deg, #d5a4ce, #75396c);

  transition-delay: 0.66s;
}

.balloon-12 {
  top: 64%;
  right: 40%;

  width: 42px;
  height: 54px;

  color: #d24a72;

  background: linear-gradient(145deg, #ffc0cf, #ca3766);

  transition-delay: 0.72s;
}

@keyframes balloon-float {
  0%,
  100% {
    margin-top: 0;
    rotate: -3deg;
  }

  50% {
    margin-top: -20px;
    rotate: 4deg;
  }
}

/* =========================================================
   PARTY CONFETTI
========================================================= */

.confetti-container {
  position: absolute;
  inset: 0;

  pointer-events: none;

  overflow: hidden;

  z-index: 50;
}

.confetti {
  position: absolute;

  top: -35px;
  left: var(--left);

  width: 8px;
  height: 16px;

  border-radius: 2px;

  opacity: 0;

  background: var(--pink);

  transform: rotate(var(--rotate));

  animation-delay: var(--delay);
  animation-duration: var(--duration);
}

.party-started .confetti {
  animation-name: confetti-fall;
  animation-timing-function: cubic-bezier(0.2, 0.7, 0.3, 1);
  animation-fill-mode: forwards;
}

.confetti:nth-child(3n) {
  background: var(--gold);
}

.confetti:nth-child(4n) {
  background: var(--purple);
}

.confetti:nth-child(5n) {
  background: #ef7195;
}

.confetti:nth-child(7n) {
  background: #f3c85b;
}

.confetti:nth-child(9n) {
  background: #b86db0;
}

.confetti:nth-child(11n) {
  width: 12px;
  height: 5px;
  border-radius: 999px;
}

.confetti:nth-child(13n) {
  width: 5px;
  height: 12px;
}

@keyframes confetti-fall {
  0% {
    opacity: 1;

    transform: translate3d(0, -40px, 0) rotate(0deg);
  }

  25% {
    transform: translate3d(-45px, 25vh, 0) rotate(180deg);
  }

  50% {
    transform: translate3d(55px, 50vh, 0) rotate(360deg);
  }

  75% {
    transform: translate3d(-35px, 75vh, 0) rotate(540deg);
  }

  100% {
    opacity: 0;

    transform: translate3d(70px, 110vh, 0) rotate(720deg);
  }
}

/* =========================================================
   BIG FIREWORKS
========================================================= */

.fireworks {
  position: absolute;
  inset: 0;

  pointer-events: none;

  z-index: 55;
}

.firework {
  position: absolute;

  width: 12px;
  height: 12px;

  border-radius: 50%;

  opacity: 0;
}

.firework-1 {
  top: 25%;
  left: 15%;
}

.firework-2 {
  top: 17%;
  right: 16%;
}

.firework-3 {
  top: 42%;
  right: 29%;
}

.firework-4 {
  top: 33%;
  left: 31%;
}

.firework-5 {
  top: 58%;
  right: 13%;
}

.firework-6 {
  top: 62%;
  left: 15%;
}

.party-started .firework {
  animation: firework-burst 1.7s ease-out forwards;
}

.party-started .firework-2 {
  animation-delay: 0.25s;
}

.party-started .firework-3 {
  animation-delay: 0.5s;
}

.party-started .firework-4 {
  animation-delay: 0.75s;
}

.party-started .firework-5 {
  animation-delay: 1s;
}

.party-started .firework-6 {
  animation-delay: 1.25s;
}

.firework i {
  position: absolute;

  left: 50%;
  top: 50%;

  width: 4px;
  height: 48px;

  border-radius: 999px;

  transform-origin: 50% 0;

  background: linear-gradient(to bottom, var(--gold), var(--pink), transparent);

  filter: drop-shadow(0 0 5px rgba(255, 195, 80, 0.7));
}

.firework i:nth-child(1) {
  transform: translate(-50%, 0) rotate(0deg);
}
.firework i:nth-child(2) {
  transform: translate(-50%, 0) rotate(22.5deg);
}
.firework i:nth-child(3) {
  transform: translate(-50%, 0) rotate(45deg);
}
.firework i:nth-child(4) {
  transform: translate(-50%, 0) rotate(67.5deg);
}
.firework i:nth-child(5) {
  transform: translate(-50%, 0) rotate(90deg);
}
.firework i:nth-child(6) {
  transform: translate(-50%, 0) rotate(112.5deg);
}
.firework i:nth-child(7) {
  transform: translate(-50%, 0) rotate(135deg);
}
.firework i:nth-child(8) {
  transform: translate(-50%, 0) rotate(157.5deg);
}
.firework i:nth-child(9) {
  transform: translate(-50%, 0) rotate(180deg);
}
.firework i:nth-child(10) {
  transform: translate(-50%, 0) rotate(202.5deg);
}
.firework i:nth-child(11) {
  transform: translate(-50%, 0) rotate(225deg);
}
.firework i:nth-child(12) {
  transform: translate(-50%, 0) rotate(247.5deg);
}
.firework i:nth-child(13) {
  transform: translate(-50%, 0) rotate(270deg);
}
.firework i:nth-child(14) {
  transform: translate(-50%, 0) rotate(292.5deg);
}
.firework i:nth-child(15) {
  transform: translate(-50%, 0) rotate(315deg);
}
.firework i:nth-child(16) {
  transform: translate(-50%, 0) rotate(337.5deg);
}

@keyframes firework-burst {
  0% {
    opacity: 0;
    transform: scale(0.05);
  }

  15% {
    opacity: 1;
    transform: scale(0.3);
  }

  55% {
    opacity: 1;
    transform: scale(1);
  }

  100% {
    opacity: 0;
    transform: scale(1.45);
  }
}

/* =========================================================
   HERO TEXT
========================================================= */

.hero h1 {
  position: relative;
  z-index: 30;

  margin: 75px auto 20px;

  max-width: 850px;

  font-family: "Lalezar", "Vazirmatn", sans-serif !important;
  font-size: clamp(35px, 5vw, 58px);
  font-weight: 400;

  line-height: 1.5;

  color: var(--pink-deep);
}

.hero h1 em {
  font-style: normal;

  background: linear-gradient(120deg, var(--pink), var(--pink-deep));

  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;

  position: relative;
}

.hero h1 em::after {
  content: "";

  position: absolute;

  left: 0;
  right: 0;
  bottom: 2px;

  height: 5px;

  border-radius: 999px;

  background: var(--gold);

  opacity: 0.45;

  transform: rotate(-2deg);
}

.heart {
  display: inline-block;

  color: var(--pink);

  animation: heart-beat 1.4s ease-in-out infinite;
}

@keyframes heart-beat {
  0%,
  100% {
    transform: scale(1);
  }

  15% {
    transform: scale(1.22);
  }

  30% {
    transform: scale(1);
  }
}

.hero p.lead {
  position: relative;
  z-index: 30;

  max-width: 650px;

  margin: 0 auto 32px;

  color: var(--ink-soft);

  font-size: 17px;
  line-height: 2;

  font-weight: 500;
}

/* =========================================================
   CTA
========================================================= */

.cta-primary {
  position: relative;

  display: inline-flex;
  align-items: center;
  gap: 12px;

  padding: 17px 30px;

  color: #fff !important;

  background: linear-gradient(135deg, var(--pink), var(--pink-deep));

  border-radius: 20px;

  font-size: 16px;
  font-weight: 800;

  box-shadow: 0 15px 30px rgba(122, 20, 64, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);

  overflow: hidden;

  transition: 0.3s ease;
}

.cta-primary::before {
  content: "";

  position: absolute;
  inset: 0;

  background: linear-gradient(
    110deg,
    transparent 20%,
    rgba(255, 255, 255, 0.4) 50%,
    transparent 80%
  );

  transform: translateX(-120%);

  transition: 0.7s ease;
}

.cta-primary:hover::before {
  transform: translateX(120%);
}

.cta-primary:hover {
  transform: translateY(-5px) scale(1.02);

  box-shadow: 0 22px 42px rgba(122, 20, 64, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
}

.cta-gift {
  position: relative;
  z-index: 2;

  font-size: 21px;
}

.cta-primary span:not(.cta-gift):not(.arrow) {
  position: relative;
  z-index: 2;
}

.arrow {
  position: relative;
  z-index: 2;

  font-size: 20px;

  transition: transform 0.3s ease;
}

.cta-primary:hover .arrow {
  transform: translateX(-6px);
}

.scroll-hint {
  margin-top: 45px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  color: var(--ink-soft);

  font-size: 12px;
}

.chev {
  width: 10px;
  height: 10px;

  border-left: 2px solid var(--pink);
  border-bottom: 2px solid var(--pink);

  transform: rotate(-45deg);

  animation: scroll-bob 1.7s infinite;
}

@keyframes scroll-bob {
  0%,
  100% {
    transform: rotate(-45deg) translate(0);
  }

  50% {
    transform: rotate(-45deg) translate(-5px, 5px);
  }
}

/* =========================================================
   STEPS
========================================================= */
/* =========================================================
   BEAUTIFUL STEPS
========================================================= */

.steps-section {
  position: relative;

  padding: 105px 24px 125px;

  background: radial-gradient(
      circle at 10% 20%,
      rgba(232, 87, 124, 0.06),
      transparent 25%
    ),
    radial-gradient(
      circle at 90% 80%,
      rgba(209, 161, 74, 0.07),
      transparent 25%
    ),
    #fff;

  overflow: hidden;
}

.steps-head {
  position: relative;

  text-align: center;

  max-width: 700px;

  margin: 0 auto 58px;
}

.section-kicker {
  display: inline-flex;

  align-items: center;

  padding: 8px 16px;

  border: 1px solid rgba(232, 87, 124, 0.13);

  border-radius: 999px;

  background: linear-gradient(
    135deg,
    rgba(232, 87, 124, 0.1),
    rgba(209, 161, 74, 0.08)
  );

  color: var(--pink-deep);

  font-size: 12px;
  font-weight: 800;

  margin-bottom: 16px;

  box-shadow: 0 8px 20px rgba(122, 20, 64, 0.05);
}

.steps-head h2 {
  margin: 0;

  font-family: "Lalezar", sans-serif !important;

  font-size: clamp(34px, 4vw, 46px);

  line-height: 1.4;

  color: var(--pink-deep);
}

.steps-head p {
  max-width: 560px;

  margin: 12px auto 0;

  color: var(--ink-soft);

  font-size: 15px;

  line-height: 2;
}

/* cards */

.steps {
  display: grid;

  grid-template-columns: repeat(3, 1fr);

  gap: 24px;

  max-width: 1050px;

  margin-inline: auto;
}

.step {
  position: relative;

  min-height: 320px;

  padding: 30px;

  border-radius: 30px;

  overflow: hidden;

  border: 1px solid rgba(122, 20, 64, 0.09);

  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.96),
    rgba(255, 247, 247, 0.92)
  );

  box-shadow: 0 15px 40px rgba(122, 20, 64, 0.07),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);

  transition: transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1),
    box-shadow 0.4s ease;

  isolation: isolate;
}

.step::before {
  content: "";

  position: absolute;

  width: 190px;
  height: 190px;

  top: -100px;
  right: -90px;

  border-radius: 50%;

  background: radial-gradient(
    circle,
    rgba(232, 87, 124, 0.15),
    transparent 68%
  );

  z-index: -1;
}

.step::after {
  content: "";

  position: absolute;

  left: 30px;
  right: 30px;
  bottom: 0;

  height: 3px;

  border-radius: 999px;

  background: linear-gradient(
    90deg,
    transparent,
    rgba(232, 87, 124, 0.55),
    transparent
  );

  opacity: 0.45;
}

.step:hover {
  transform: translateY(-10px);

  box-shadow: 0 25px 55px rgba(122, 20, 64, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);
}

/* number */

.step-number {
  position: absolute;

  top: 23px;
  left: 27px;

  font-family: "Lalezar", sans-serif !important;

  font-size: 19px;

  color: rgba(122, 20, 64, 0.18);

  direction: ltr;
}

/* icon */

.step-icon-wrap {
  width: 68px;
  height: 68px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 21px;

  background: linear-gradient(145deg, #fff, #fff0f3);

  border: 1px solid rgba(232, 87, 124, 0.13);

  box-shadow: 0 12px 25px rgba(122, 20, 64, 0.08), inset 0 1px 0 #fff;

  margin-bottom: 25px;
}

.step-icon {
  font-size: 32px;

  filter: drop-shadow(0 5px 8px rgba(122, 20, 64, 0.12));
}

/* text */

.step-content {
  position: relative;
}

.step-label {
  display: block;

  margin-bottom: 5px;

  color: var(--pink);

  font-size: 11px;

  font-weight: 800;
}

.step h3 {
  margin: 0;

  color: var(--ink);

  font-family: "Vazirmatn", sans-serif;

  font-size: 20px;

  font-weight: 900;
}

.step p {
  max-width: 260px;

  margin-top: 11px;

  color: var(--ink-soft);

  font-size: 13px;

  line-height: 2;
}

/* arrow */

.step-arrow {
  position: absolute;

  bottom: 25px;
  left: 28px;

  width: 38px;
  height: 38px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;

  color: var(--pink-deep);

  background: rgba(232, 87, 124, 0.08);

  font-size: 18px;

  transition: 0.3s ease;
}

.step:hover .step-arrow {
  transform: translateX(-5px);

  background: rgba(232, 87, 124, 0.14);
}

/* highlight card */

.step.highlight {
  transform: translateY(-12px);

  border: 1px solid rgba(255, 255, 255, 0.25);

  color: #fff;

  background: radial-gradient(
      circle at 90% 0%,
      rgba(255, 255, 255, 0.25),
      transparent 32%
    ),
    radial-gradient(
      circle at 0% 100%,
      rgba(255, 180, 200, 0.2),
      transparent 35%
    ),
    linear-gradient(145deg, #e85c82, #9b2451 60%, #691238);

  box-shadow: 0 28px 60px rgba(122, 20, 64, 0.23),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.step.highlight:hover {
  transform: translateY(-20px);
}

.step.highlight::before {
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.18),
    transparent 68%
  );
}

.step.highlight::after {
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 220, 150, 0.8),
    transparent
  );

  opacity: 0.8;
}

.step.highlight .step-number {
  color: rgba(255, 255, 255, 0.35);
}

.step.highlight .step-icon-wrap {
  border-color: rgba(255, 255, 255, 0.2);

  background: rgba(255, 255, 255, 0.14);

  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3),
    0 12px 25px rgba(80, 5, 30, 0.12);
}

.step.highlight .step-label {
  color: var(--gold-light);
}

.step.highlight h3,
.step.highlight p {
  color: #fff;
}

.step.highlight p {
  opacity: 0.88;
}

.step.highlight .step-arrow {
  color: #fff;

  background: rgba(255, 255, 255, 0.12);
}

/* =========================================================
   GIFTS
========================================================= */

.gifts-section {
  position: relative;

  padding: 110px 24px 120px;

  background: linear-gradient(180deg, var(--cream), #f9e7e4);

  overflow: hidden;
}

.gift-background {
  position: absolute;

  inset: 0;

  background: radial-gradient(
      circle at 15% 25%,
      rgba(232, 87, 124, 0.15),
      transparent 25%
    ),
    radial-gradient(
      circle at 85% 65%,
      rgba(199, 154, 70, 0.15),
      transparent 25%
    );

  pointer-events: none;
}

.gifts-head {
  position: relative;

  max-width: 680px;

  text-align: center;

  margin: 0 auto 60px;
}

.gifts-head h2 {
  font-family: "Lalezar", sans-serif !important;

  font-size: 42px;

  color: var(--pink-deep);
}

.gifts-head h2 em {
  font-style: normal;

  color: var(--pink);
}

.gifts-head p {
  margin-top: 13px;

  color: var(--ink-soft);

  font-size: 15px;

  line-height: 2;
}

/* gift grid */

.gift-grid {
  position: relative;

  display: grid;

  grid-template-columns: repeat(4, 1fr);

  gap: 35px 26px;

  max-width: 980px;

  margin-inline: auto;
}

.gift {
  position: relative;
  aspect-ratio: 1;
  cursor: pointer;
  transition: transform 0.45s cubic-bezier(0.2, 0.9, 0.3, 1.3), filter 0.3s ease;

  filter: drop-shadow(0 17px 16px rgba(122, 20, 64, 0.13));
}

/* حفظ حالت کجی */

.gift-1 {
  transform: rotate(-5deg);
}

.gift-2 {
  transform: rotate(4deg);
}

.gift-3 {
  transform: rotate(-3deg);
}

.gift-4 {
  transform: rotate(5deg);
}

.gift-5 {
  transform: rotate(3deg);
}

.gift-6 {
  transform: rotate(-5deg);
}

.gift-7 {
  transform: rotate(4deg);
}

.gift-8 {
  transform: rotate(-3deg);
}

.gift:hover {
  transform: rotate(0deg) translateY(-14px) scale(1.07);

  filter: drop-shadow(0 25px 28px rgba(122, 20, 64, 0.2));

  z-index: 10;
}

.gift-shadow {
  position: absolute;

  bottom: -13px;
  left: 12%;
  right: 12%;

  height: 24px;

  border-radius: 50%;

  background: rgba(122, 20, 64, 0.15);

  filter: blur(8px);
}

.box {
  position: absolute;

  inset: 0;

  border-radius: 24px;

  overflow: visible;

  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.45),
    inset 0 -10px 20px rgba(122, 20, 64, 0.1);
}

.gift-1 .box {
  background: linear-gradient(145deg, #ffc2d0, #e8577c);
}

.gift-2 .box {
  background: linear-gradient(145deg, #f7b0c2, #c93363);
}

.gift-3 .box {
  background: linear-gradient(145deg, #f7df9c, #bd8833);
}

.gift-4 .box {
  background: linear-gradient(145deg, #e96f95, #76153e);
}

.gift-5 .box {
  background: linear-gradient(145deg, #f6c8d3, #d85a7e);
}

.gift-6 .box {
  background: linear-gradient(145deg, #d79cce, #7a3d73);
}

.gift-7 .box {
  background: linear-gradient(145deg, #f6d98c, #a87322);
}

.gift-8 .box {
  background: linear-gradient(145deg, #f487a3, #821a47);
}

/* top */

.box-top {
  position: absolute;

  top: -10px;
  left: -2%;

  width: 104%;
  height: 25%;

  border-radius: 16px 16px 9px 9px;

  background: linear-gradient(180deg, rgba(255, 255, 255, 0.25), transparent);

  box-shadow: 0 8px 10px rgba(122, 20, 64, 0.1);
}

/* ribbons */

.ribbon-v {
  position: absolute;

  top: -2%;
  bottom: -2%;

  left: 50%;

  width: 18%;

  transform: translateX(-50%);

  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.28),
    rgba(255, 255, 255, 0.68),
    rgba(255, 255, 255, 0.28)
  );

  box-shadow: 0 0 10px rgba(255, 255, 255, 0.15);
}

.ribbon-h {
  position: absolute;

  left: -2%;
  right: -2%;

  top: 49%;

  height: 18%;

  transform: translateY(-50%);

  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.25),
    rgba(255, 255, 255, 0.65),
    rgba(255, 255, 255, 0.22)
  );
}

/* bow */

.bow {
  position: absolute;

  top: -19%;

  left: 50%;

  width: 55%;

  height: 34%;

  transform: translateX(-50%);

  z-index: 10;
}

.bow-left,
.bow-right {
  position: absolute;

  top: 5%;

  width: 52%;
  height: 85%;

  border-radius: 65% 35% 65% 35% / 55% 55% 45% 45%;

  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.9),
    rgba(255, 255, 255, 0.35)
  );

  box-shadow: inset -5px -4px 8px rgba(122, 20, 64, 0.12),
    0 7px 10px rgba(122, 20, 64, 0.13);
}

.bow-left {
  left: 0;

  transform: rotate(-25deg) skewX(-8deg);
}

.bow-right {
  right: 0;

  transform: rotate(25deg) scaleX(-1) skewX(-8deg);
}

.bow-center {
  position: absolute;

  top: 35%;
  left: 50%;

  width: 24%;
  height: 35%;

  transform: translate(-50%, -50%);

  border-radius: 50%;

  background: linear-gradient(145deg, #fff, #e6d8dc);

  box-shadow: 0 5px 8px rgba(122, 20, 64, 0.18);
}

/* shine */

.box-shine {
  position: absolute;

  top: 5%;
  left: -35%;

  width: 25%;
  height: 90%;

  transform: rotate(18deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.32),
    transparent
  );

  opacity: 0;

  transition: 0.5s ease;
}

.gift:hover .box-shine {
  left: 120%;
  opacity: 1;

  transition: left 0.75s ease, opacity 0.1s ease;
}

/* tag */

.tag {
  position: absolute;

  right: 9px;
  bottom: 9px;

  z-index: 20;

  padding: 5px 10px;

  border-radius: 999px;

  color: var(--pink-deep);

  background: rgba(255, 255, 255, 0.92);

  font-family: "Lalezar", sans-serif !important;

  font-size: 13px;

  box-shadow: 0 5px 12px rgba(0, 0, 0, 0.12);
}

/* peek */

.peek {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 30;
  border: 10px solid transparent;
  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  gap: 4px;

  padding: 15px;

  text-align: center;

  border-radius: 24px;

  color: #fff;

  background: linear-gradient(
    145deg,
    rgba(70, 8, 35, 0.74),
    rgba(122, 20, 64, 0.86)
  );

  backdrop-filter: blur(3px);

  opacity: 0;

  transition: 0.35s ease;
}

.gift:hover .peek {
  opacity: 1;
}

.peek-icon {
  font-size: 28px;

  margin-bottom: 5px;
}

.peek strong {
  font-size: 14px;
}

.peek small {
  font-size: 11px;

  opacity: 0.85;
}

.gifts-note {
  position: relative;

  margin-top: 55px;

  text-align: center;

  color: var(--ink-soft);

  font-size: 13px;

  font-weight: 600;
}

.gifts-note span {
  color: var(--gold);

  margin-inline: 7px;
}

/* =========================================================
   FINAL BANNER
========================================================= */

.final-banner {
  position: relative;

  min-height: 540px;

  padding: 110px 24px;

  display: flex;
  align-items: center;
  justify-content: center;

  text-align: center;

  overflow: hidden;

  isolation: isolate;

  color: #fff;

  background: radial-gradient(circle at 25% 20%, #e8577c, transparent 30%),
    radial-gradient(circle at 80% 80%, #a52c5b, transparent 35%),
    linear-gradient(135deg, #7a1440, #450921);
}

.final-banner::before {
  content: "";

  position: absolute;
  inset: 0;

  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.13) 1.5px,
    transparent 2px
  );

  background-size: 38px 38px;

  opacity: 0.45;
}

.final-glow {
  position: absolute;

  width: 400px;
  height: 400px;

  border-radius: 50%;

  filter: blur(70px);

  opacity: 0.3;

  z-index: -1;
}

.final-glow-1 {
  top: -250px;
  right: -100px;

  background: var(--pink);
}

.final-glow-2 {
  bottom: -250px;
  left: -100px;

  background: var(--gold);
}

.final-banner .inner {
  position: relative;

  z-index: 20;

  width: 100%;
  max-width: 650px;
}

.final-eyebrow {
  color: #fff;

  background: rgba(255, 255, 255, 0.12);

  border-color: rgba(255, 255, 255, 0.25);
}

.final-cake-icon {
  margin-top: 24px;

  font-size: 52px;

  filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.2));

  animation: cake-icon-float 2.8s ease-in-out infinite;
}

@keyframes cake-icon-float {
  0%,
  100% {
    transform: translateY(0) rotate(-3deg);
  }

  50% {
    transform: translateY(-8px) rotate(3deg);
  }
}

.final-banner h2 {
  position: relative;

  margin: 18px 0 15px;

  color: #fff;

  font-family: "Lalezar", sans-serif !important;

  font-size: clamp(36px, 5vw, 52px);

  line-height: 1.45;

  text-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

.final-banner h2 strong {
  display: block;

  color: var(--gold-light);

  font-weight: 400;
}

.final-banner p {
  position: relative;

  max-width: 500px;

  margin: 0 auto 35px;

  color: rgba(255, 255, 255, 0.88);

  font-size: 16px;

  line-height: 2;
}

.final-button {
  position: relative;

  display: inline-flex;

  align-items: center;

  gap: 12px;

  padding: 17px 30px;

  border-radius: 20px;

  color: var(--pink-deep);

  background: #fff;

  font-weight: 900;

  font-size: 16px;

  overflow: hidden;

  box-shadow: 0 18px 35px rgba(0, 0, 0, 0.22);

  transition: 0.3s ease;
}
.final-button span {
  color: var(--pink-deep);
}
.final-button:hover {
  transform: translateY(-6px) scale(1.03);

  box-shadow: 0 25px 45px rgba(0, 0, 0, 0.28);
}

.final-button-icon,
.final-arrow,
.final-button > span:not(.final-button-glow) {
  position: relative;

  z-index: 2;
}

.final-button-icon {
  font-size: 21px;
}

.final-arrow {
  font-size: 20px;

  transition: 0.3s ease;
}

.final-button:hover .final-arrow {
  transform: translateX(-5px);
}

.final-button-glow {
  position: absolute;

  inset: -50%;

  background: linear-gradient(
    110deg,
    transparent 40%,
    rgba(232, 87, 124, 0.15),
    transparent 60%
  );

  transform: translateX(-80%);

  animation: final-shine 3s infinite;
}

@keyframes final-shine {
  0% {
    transform: translateX(-80%);
  }

  45%,
  100% {
    transform: translateX(80%);
  }
}

.final-particles {
  position: absolute;
  inset: 0;

  pointer-events: none;
}

.final-particles span {
  position: absolute;

  color: rgba(255, 255, 255, 0.28);

  animation: particle-float 4s ease-in-out infinite;
}

.final-particles span:nth-child(1) {
  top: 20%;
  right: 10%;
}

.final-particles span:nth-child(2) {
  top: 60%;
  right: 20%;
}

.final-particles span:nth-child(3) {
  top: 30%;
  left: 14%;
}

.final-particles span:nth-child(4) {
  top: 70%;
  left: 10%;
}

.final-particles span:nth-child(5) {
  top: 15%;
  left: 35%;
}

.final-particles span:nth-child(6) {
  top: 80%;
  right: 38%;
}

.final-particles span:nth-child(7) {
  top: 40%;
  right: 5%;
}

.final-particles span:nth-child(8) {
  top: 50%;
  left: 4%;
}

@keyframes particle-float {
  0%,
  100% {
    transform: translateY(0) rotate(0);
  }

  50% {
    transform: translateY(-18px) rotate(90deg);
  }
}

/* =========================================================
   FOOTER
========================================================= */

footer {
  padding: 35px 24px 100px;

  display: flex;
  flex-direction: column;
  align-items: center;

  gap: 7px;

  color: var(--ink-soft);

  background: var(--cream);

  font-size: 13px;
}

footer .logo-mark {
  font-size: 23px;
}

/* =========================================================
   MOBILE STICKY
========================================================= */

.sticky-mobile {
  display: none;

  position: fixed;

  bottom: 0;
  left: 0;
  right: 0;

  z-index: 200;

  padding: 11px 14px calc(11px + env(safe-area-inset-bottom));

  background: rgba(255, 247, 244, 0.9);

  backdrop-filter: blur(18px);

  border-top: 1px solid rgba(122, 20, 64, 0.08);
}

.sticky-mobile a {
  display: flex;
  align-items: center;
  justify-content: center;

  gap: 10px;

  padding: 14px;

  border-radius: 16px;

  color: #fff;

  background: linear-gradient(135deg, var(--pink), var(--pink-deep));

  font-weight: 800;

  box-shadow: 0 10px 25px rgba(122, 20, 64, 0.25);
}

/* =========================================================
   PARTY MODE
========================================================= */

.party-started .hero {
  animation: party-screen 0.9s ease;
}

.party-started .hero-inner {
  animation: party-content 0.8s ease;
}

.party-started .cake {
  animation: cake-party 0.9s ease, cake-float 4s 1s ease-in-out infinite;
}

@keyframes party-screen {
  0% {
    filter: brightness(1);
  }

  45% {
    filter: brightness(1.12) saturate(1.15);
  }

  100% {
    filter: brightness(1);
  }
}

@keyframes party-content {
  0% {
    transform: scale(0.98);
  }

  45% {
    transform: scale(1.025);
  }

  100% {
    transform: scale(1);
  }
}

@keyframes cake-party {
  0% {
    transform: translateY(0) rotate(0);
  }

  30% {
    transform: translateY(-12px) rotate(-2deg);
  }

  60% {
    transform: translateY(-5px) rotate(2deg);
  }

  100% {
    transform: translateY(0) rotate(0);
  }
}

/* =========================================================
   RESPONSIVE
========================================================= */

@media (max-width: 1000px) {
  .gift-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .hero {
    min-height: 880px;
  }
}

@media (max-width: 860px) {
  .steps {
    grid-template-columns: 1fr;
    max-width: 520px;
  }

  .step.highlight {
    transform: none;
  }

  .gift-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .balloon-3,
  .balloon-4 {
    display: none;
  }
  .layer-1 {
    height: 60px;
  }

  .layer-2 {
    height: 62px;
  }

  .layer-3 {
    height: 64px;
  }
}

@media (max-width: 650px) {
  .topbar {
    height: 64px;
    padding-inline: 16px;
  }

  .logo-mark {
    font-size: 21px;
  }

  .cta-mini {
    padding: 9px 14px;
    font-size: 12px;
  }

  .hero {
    min-height: 850px;

    padding: 10px 16px 90px;
  }

  .hero-inner {
    max-width: 100%;
  }

  .cake-stage {
    width: 100%;

    min-height: 380px;

    margin-top: 35px;
  }

  .cake {
    width: min(330px, 86vw);

    margin-top: 60px;
  }
  .candle {
    width: 100%;
    height: 100%;
  }
  .candle-wrap {
    position: relative;

    width: 18px;
    height: 65px;
  }
  .candles {
    width: 185px;
    top: -50px;
  }

  .hero h1 {
    margin-top: 70px;

    font-size: 34px;

    line-height: 1.55;
  }

  .hero p.lead {
    font-size: 14px;

    line-height: 2;
  }

  .blow-button {
    bottom: -20px;

    min-width: 215px;

    min-height: 65px;
  }

  .balloon {
    width: 52px;
    height: 67px;
  }

  .balloon-1 {
    left: -5px;
  }

  .balloon-2 {
    right: -5px;
  }

  .balloon-5 {
    left: 18%;
    width: 42px;
    height: 54px;
  }

  .spark-3,
  .spark-4,
  .spark-9,
  .spark-10 {
    display: none;
  }

  .steps-section {
    padding: 70px 16px;
  }

  .steps-head h2 {
    font-size: 32px;
  }

  .gifts-section {
    padding: 80px 16px;
  }

  .gifts-head h2 {
    font-size: 34px;
  }

  .gift-grid {
    grid-template-columns: repeat(2, 1fr);

    gap: 25px 16px;
  }

  .gift:hover {
    transform: rotate(0) translateY(-8px) scale(1.03);
  }

  .final-banner {
    min-height: 500px;

    padding: 80px 18px;
  }

  .final-banner h2 {
    font-size: 34px;
  }

  .final-banner p {
    font-size: 14px;
  }

  .sticky-mobile {
    display: block;
  }

  footer {
    padding-bottom: 100px;
  }
}

@media (max-width: 420px) {
  .eyebrow {
    font-size: 16px;
    padding: 8px 12px;
  }

  .hero {
    min-height: 820px;
  }

  .cake {
    width: 290px;
  }

  .candles {
    width: 165px;
  }

  .hero h1 {
    font-size: 29px;
  }

  .gift-grid {
    gap: 20px 12px;
  }

  .gift {
    border-radius: 18px;
  }

  .box,
  .peek {
    border-radius: 18px;
  }

  .peek-icon {
    font-size: 22px;
  }

  .peek strong {
    font-size: 12px;
  }

  .peek small {
    font-size: 9px;
  }

  .tag {
    font-size: 10px;
    padding: 4px 7px;
  }

  .final-banner h2 {
    font-size: 30px;
  }
}
</style>
